'use client';

import { useMemo, useState } from 'react';
import { buildTableSnippet, makeEmptyCells } from '@/lib/browser-widgetgen-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const MAX_ROWS = 20;
const MAX_COLS = 12;

export default function BrowserHtmlTableGenerator() {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [headerRow, setHeaderRow] = useState(true);
  const [striped, setStriped] = useState(true);
  const [bordered, setBordered] = useState(true);
  const [inlineCss, setInlineCss] = useState(false);
  const [cells, setCells] = useState<string[][]>(() => {
    const grid = makeEmptyCells(3, 3);
    grid[0] = ['Name', 'Role', 'Location'];
    grid[1] = ['Ada', 'Engineer', 'London'];
    grid[2] = ['Grace', 'Designer', 'New York'];
    return grid;
  });
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  function resize(nextRows: number, nextCols: number) {
    const r = Math.max(1, Math.min(MAX_ROWS, nextRows));
    const c = Math.max(1, Math.min(MAX_COLS, nextCols));
    setRows(r);
    setCols(c);
    setCells((prev) => makeEmptyCells(r, c, prev));
  }

  function setCell(r: number, c: number, value: string) {
    setCells((prev) => {
      const next = prev.map((row) => [...row]);
      if (next[r]) next[r][c] = value;
      return next;
    });
  }

  const snippet = useMemo(
    () => buildTableSnippet({ rows, cols, headerRow, striped, bordered, inlineCss, cells }),
    [rows, cols, headerRow, striped, bordered, inlineCss, cells],
  );

  async function handleCopy() {
    try {
      await copyText(snippet);
      setCopyMessage('HTML copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the HTML.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Client-side</span>
        <h2 className="tool-live-title">HTML table generator</h2>
        <p className="tool-live-subtitle">
          Set rows and columns, edit each cell, toggle the header row, striping, and borders, then
          copy clean HTML. Built in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Rows</span>
              <input
                max={MAX_ROWS}
                min={1}
                onChange={(e) => resize(Number(e.target.value), cols)}
                type="number"
                value={rows}
              />
            </label>
            <label className="tool-live-field">
              <span>Columns</span>
              <input
                max={MAX_COLS}
                min={1}
                onChange={(e) => resize(rows, Number(e.target.value))}
                type="number"
                value={cols}
              />
            </label>
            <label className="tool-live-field">
              <span>Header row</span>
              <select
                onChange={(e) => setHeaderRow(e.target.value === 'yes')}
                value={headerRow ? 'yes' : 'no'}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Striped rows</span>
              <select
                onChange={(e) => setStriped(e.target.value === 'yes')}
                value={striped ? 'yes' : 'no'}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Borders</span>
              <select
                onChange={(e) => setBordered(e.target.value === 'yes')}
                value={bordered ? 'yes' : 'no'}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Style output</span>
              <select
                onChange={(e) => setInlineCss(e.target.value === 'inline')}
                value={inlineCss ? 'inline' : 'css'}
              >
                <option value="css">Separate CSS block</option>
                <option value="inline">Inline styles</option>
              </select>
            </label>
            <div className="tool-live-field tool-live-field-full">
              <span>Cell contents</span>
              <div style={{ overflowX: 'auto', marginTop: 6 }}>
                <table style={{ borderCollapse: 'collapse' }}>
                  <tbody>
                    {Array.from({ length: rows }).map((_, r) => (
                      <tr key={r}>
                        {Array.from({ length: cols }).map((__, c) => (
                          <td key={c} style={{ padding: 2 }}>
                            <input
                              onChange={(e) => setCell(r, c, e.target.value)}
                              style={{
                                width: 96,
                                fontWeight: headerRow && r === 0 ? 600 : 400,
                              }}
                              type="text"
                              value={cells[r]?.[c] ?? ''}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy HTML
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              padding: 16,
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.1)',
              background: '#ffffff',
              overflowX: 'auto',
            }}
          >
            <table
              style={{
                borderCollapse: 'collapse',
                width: '100%',
                fontSize: 14,
                border: bordered ? '1px solid #d1d5db' : undefined,
              }}
            >
              <tbody>
                {Array.from({ length: rows }).map((_, r) => {
                  const isHeader = headerRow && r === 0;
                  const zebra = striped && !isHeader && r % 2 === 0;
                  return (
                    <tr
                      key={r}
                      style={{ background: isHeader ? '#f3f4f6' : zebra ? '#f9fafb' : undefined }}
                    >
                      {Array.from({ length: cols }).map((__, c) => {
                        const content = cells[r]?.[c] ?? '';
                        const cellStyle: React.CSSProperties = {
                          padding: '8px 12px',
                          border: bordered ? '1px solid #d1d5db' : undefined,
                          textAlign: isHeader ? 'left' : undefined,
                          fontWeight: isHeader ? 600 : 400,
                        };
                        return isHeader ? (
                          <th key={c} style={cellStyle}>
                            {content}
                          </th>
                        ) : (
                          <td key={c} style={cellStyle}>
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>HTML output</span>
            <textarea readOnly rows={12} value={snippet} />
          </label>
          <p className="tool-live-panel-note">Generated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
